import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ArrowLeft,
  Pencil,
  Eraser,
  Undo2,
  Redo2,
  Trash2,
  Sparkles,
  Loader2,
  RotateCcw,
  Download,
  Check,
  RefreshCw,
  Plus,
  ChevronLeft,
  ChevronRight,
  X,
  Type,
  Wand2,
  MessageSquare,
  Lightbulb,
  LayoutGrid,
  Minus,
  ImagePlus,
} from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { CreativeChat } from "@/components/creative-chat";
import { cn } from "@/lib/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useRoute, useLocation } from "wouter";
import type { Creative, SavedAngle } from "@shared/schema";
import { Checkbox } from "@/components/ui/checkbox";

function proxyUrl(url: string): string {
  if (!url) return url;
  if (url.startsWith("/")) return url;
  return `/api/proxy-image?url=${encodeURIComponent(url)}`;
}

interface MaskStroke {
  x: number;
  y: number;
  size: number;
}

interface HistoryEntry {
  maskStrokes: MaskStroke[];
}

type EditorTool = "draw" | "erase" | "text-select";

export default function CreativeEditor() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/creative/:id/edit");
  const creativeId = params?.id ? Number(params.id) : null;
  const queryClient = useQueryClient();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);

  const [tool, setTool] = useState<EditorTool>("draw");
  const [brushSize, setBrushSize] = useState(40);
  const [maskStrokes, setMaskStrokes] = useState<MaskStroke[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [baseImage, setBaseImage] = useState<HTMLImageElement | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 600 });
  const [scale, setScale] = useState(1);

  const [history, setHistory] = useState<HistoryEntry[]>([{ maskStrokes: [] }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const historyRef = useRef(history);
  const historyIndexRef = useRef(historyIndex);
  const maskStrokesRef = useRef(maskStrokes);
  historyRef.current = history;
  historyIndexRef.current = historyIndex;
  maskStrokesRef.current = maskStrokes;

  const [inpainting, setInpainting] = useState(false);
  const [inpaintResult, setInpaintResult] = useState<string | null>(null);
  const [inpaintResultImage, setInpaintResultImage] = useState<HTMLImageElement | null>(null);

  const [refImage, setRefImage] = useState<File | null>(null);
  const [refImagePreview, setRefImagePreview] = useState<string | null>(null);
  const refImageInputRef = useRef<HTMLInputElement>(null);
  const refImagePreviewRef = useRef(refImagePreview);
  refImagePreviewRef.current = refImagePreview;

  useEffect(() => {
    return () => {
      if (refImagePreviewRef.current) URL.revokeObjectURL(refImagePreviewRef.current);
    };
  }, []);

  const [editHistoryIndex, setEditHistoryIndex] = useState(-1);

  const [textBoxDrawing, setTextBoxDrawing] = useState(false);
  const [textBoxStart, setTextBoxStart] = useState<{ x: number; y: number } | null>(null);
  const [textBoxEnd, setTextBoxEnd] = useState<{ x: number; y: number } | null>(null);
  const [textBoxConfirmed, setTextBoxConfirmed] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [textSidebarOpen, setTextSidebarOpen] = useState(false);
  const [taglines, setTaglines] = useState<string[]>([""]);
  const [generatingVariations, setGeneratingVariations] = useState(false);
  const [variationProgress, setVariationProgress] = useState<{ current: number; total: number } | null>(null);
  const [suggestingHeadlines, setSuggestingHeadlines] = useState(false);

  const [anglesSidebarOpen, setAnglesSidebarOpen] = useState(false);
  const [selectedAngleIds, setSelectedAngleIds] = useState<Set<number>>(new Set());

  const [selectedCreativeId, setSelectedCreativeId] = useState<number | null>(creativeId);
  const [chatOpen, setChatOpen] = useState(false);
  const [variationsMenuOpen, setVariationsMenuOpen] = useState(false);
  const [variationsQuantity, setVariationsQuantity] = useState(2);
  const [variationsCreativity, setVariationsCreativity] = useState(0);
  const [creatingVariations, setCreatingVariations] = useState(false);
  const [variationsCreationProgress, setVariationsCreationProgress] = useState<string | null>(null);
  const variationsMenuRef = useRef<HTMLDivElement>(null);

  const AD_STYLES = [
    "Problem-Solution", "Benefits", "Features", "Reviews",
    "Listicle", "Advertorial", "Us vs Them", "Education",
    "UGC Style", "Before & After", "Founder Story",
    "Social Proof", "Urgency", "Product Launch", "Sale",
    "How It Works", "Myth Busting", "Seasonal",
  ];
  const [stylesMenuOpen, setStylesMenuOpen] = useState(false);
  const [selectedStyles, setSelectedStyles] = useState<Set<string>>(new Set());
  const [stylesQuantity, setStylesQuantity] = useState(1);
  const [generatingStyles, setGeneratingStyles] = useState(false);
  const stylesMenuRef = useRef<HTMLDivElement>(null);
  const [imageModel, setImageModel] = useState<"seedream-v5-lite" | "nano-banana-2">("seedream-v5-lite");

  const { data: creative } = useQuery<Creative>({
    queryKey: ["/api/creatives", selectedCreativeId],
    queryFn: async () => {
      const res = await fetch(`/api/creatives/${selectedCreativeId}`);
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!selectedCreativeId,
    placeholderData: (prev) => prev,
  });

  const { data: savedAngles = [] } = useQuery<SavedAngle[]>({
    queryKey: ["/api/brands", creative?.brandId, "saved-angles"],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${creative!.brandId}/saved-angles`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!creative?.brandId,
  });

  const { data: groupCreatives = [] } = useQuery<Creative[]>({
    queryKey: creative?.groupId
      ? [`/api/creative-groups/${creative.groupId}/creatives`]
      : ["/api/creatives"],
    enabled: !!creative,
    refetchInterval: (query) => {
      const data = query.state.data as Creative[] | undefined;
      if (data?.some((c) => c.status === "generating" || c.status === "pending")) return 3000;
      return false;
    },
  });

  const sidebarCreatives = groupCreatives.filter((c) => c.status === "completed" && !c.adStyle);
  const completedCreatives = groupCreatives.filter((c) => c.status === "completed");

  const computedSourceUrl = creative?.adStyle
    ? creative.referenceImageUrls?.[0] || ""
    : creative?.imageUrl || "";
  const lastSourceUrlRef = useRef(computedSourceUrl);
  if (computedSourceUrl) lastSourceUrlRef.current = computedSourceUrl;
  const sourceCreativeUrl = computedSourceUrl || lastSourceUrlRef.current;

  const sourceCreative = creative?.adStyle
    ? groupCreatives.find((c) => c.imageUrl === sourceCreativeUrl) || null
    : creative;
  const styleVariationsForCurrent = groupCreatives.filter((c) =>
    c.adStyle && c.referenceImageUrls?.includes(sourceCreativeUrl)
  );
  const generatingStyleVariations = styleVariationsForCurrent.filter((c) => c.status === "generating" || c.status === "pending");
  const completedStyleVariations = styleVariationsForCurrent.filter((c) => c.status === "completed");

  const { data: editVersions = [], refetch: refetchVersions } = useQuery<{ id: number; creativeId: number; imageUrl: string; prompt: string; createdAt: string }[]>({
    queryKey: [`/api/creatives/${selectedCreativeId}/versions`],
    queryFn: async () => {
      const res = await fetch(`/api/creatives/${selectedCreativeId}/versions`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!selectedCreativeId,
  });

  const editHistory = [
    ...(creative?.imageUrl ? [{ id: null as number | null, imageUrl: creative.imageUrl, prompt: "Original", creativeId: selectedCreativeId }] : []),
    ...editVersions.map((v) => ({ id: v.id as number | null, imageUrl: v.imageUrl, prompt: v.prompt, creativeId: selectedCreativeId })),
  ];

  const sourceEntry = sourceCreative && sourceCreative.id !== selectedCreativeId
    ? { id: null as number | null, imageUrl: sourceCreative.imageUrl || "", prompt: "Original", creativeId: sourceCreative.id }
    : null;

  useEffect(() => {
    if (!creative?.imageUrl) return;
    const currentImageUrl = inpaintResult || creative.imageUrl;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setBaseImage(img);
      const containerMaxW = Math.min(window.innerWidth - 400, 720);
      const containerMaxH = window.innerHeight - 280;
      const s = Math.min(containerMaxW / img.naturalWidth, containerMaxH / img.naturalHeight, 1);
      setScale(s);
      setCanvasSize({
        width: Math.round(img.naturalWidth * s),
        height: Math.round(img.naturalHeight * s),
      });
    };
    img.src = proxyUrl(currentImageUrl);
  }, [creative?.imageUrl, inpaintResult]);

  const resetMaskState = useCallback(() => {
    setMaskStrokes([]);
    setHistory([{ maskStrokes: [] }]);
    setHistoryIndex(0);
  }, []);

  useEffect(() => {
    resetMaskState();
    setInpaintResult(null);
    setInpaintResultImage(null);
    setPrompt("");
    setEditHistoryIndex(-1);
  }, [selectedCreativeId, resetMaskState]);

  const addToHistory = useCallback((strokes: MaskStroke[]) => {
    const idx = historyIndexRef.current;
    const prev = historyRef.current;
    const newHistory = prev.slice(0, idx + 1);
    newHistory.push({ maskStrokes: [...strokes] });
    if (newHistory.length > 50) newHistory.shift();
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, []);

  const undo = useCallback(() => {
    const idx = historyIndexRef.current;
    const hist = historyRef.current;
    if (idx > 0) {
      const newIndex = idx - 1;
      const state = hist[newIndex];
      setMaskStrokes(state.maskStrokes);
      setHistoryIndex(newIndex);
    }
  }, []);

  const redo = useCallback(() => {
    const idx = historyIndexRef.current;
    const hist = historyRef.current;
    if (idx < hist.length - 1) {
      const newIndex = idx + 1;
      const state = hist[newIndex];
      setMaskStrokes(state.maskStrokes);
      setHistoryIndex(newIndex);
    }
  }, []);

  const clearMask = useCallback(() => {
    setMaskStrokes([]);
    addToHistory([]);
  }, [addToHistory]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !baseImage) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasSize.width * dpr;
    canvas.height = canvasSize.height * dpr;
    canvas.style.width = `${canvasSize.width}px`;
    canvas.style.height = `${canvasSize.height}px`;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);

    ctx.drawImage(baseImage, 0, 0, canvasSize.width, canvasSize.height);

    if (maskStrokes.length > 0) {
      const maskCanvas = maskCanvasRef.current;
      if (maskCanvas) {
        const maskCtx = maskCanvas.getContext("2d");
        if (maskCtx) {
          maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
          maskCtx.fillStyle = "white";

          maskStrokes.forEach((stroke) => {
            maskCtx.beginPath();
            maskCtx.arc(stroke.x, stroke.y, stroke.size / 2, 0, 2 * Math.PI);
            maskCtx.fill();
          });

          ctx.save();
          ctx.globalAlpha = 0.35;
          ctx.globalCompositeOperation = "source-over";

          const overlayCanvas = document.createElement("canvas");
          overlayCanvas.width = maskCanvas.width;
          overlayCanvas.height = maskCanvas.height;
          const overlayCtx = overlayCanvas.getContext("2d")!;
          overlayCtx.fillStyle = "#6366F1";
          overlayCtx.fillRect(0, 0, overlayCanvas.width, overlayCanvas.height);
          overlayCtx.globalCompositeOperation = "destination-in";
          overlayCtx.drawImage(maskCanvas, 0, 0);

          ctx.drawImage(overlayCanvas, 0, 0);
          ctx.restore();
        }
      }
    }

    if (textBoxDrawing && textBoxStart && textBoxEnd) {
      const bx = Math.min(textBoxStart.x, textBoxEnd.x);
      const by = Math.min(textBoxStart.y, textBoxEnd.y);
      const bw = Math.abs(textBoxEnd.x - textBoxStart.x);
      const bh = Math.abs(textBoxEnd.y - textBoxStart.y);
      ctx.fillStyle = "rgba(234, 88, 12, 0.08)";
      ctx.fillRect(bx, by, bw, bh);
      ctx.strokeStyle = "#ea580c";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.strokeRect(bx, by, bw, bh);
      ctx.setLineDash([]);
    }

    if (textBoxConfirmed && !textSidebarOpen) {
      const { x, y, w, h } = textBoxConfirmed;
      ctx.fillStyle = "rgba(234, 88, 12, 0.08)";
      ctx.fillRect(x, y, w, h);
      ctx.strokeStyle = "#ea580c";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
    }
  }, [baseImage, maskStrokes, canvasSize, textBoxDrawing, textBoxStart, textBoxEnd, textBoxConfirmed, textSidebarOpen]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  useEffect(() => {
    if (maskCanvasRef.current) {
      maskCanvasRef.current.width = canvasSize.width;
      maskCanvasRef.current.height = canvasSize.height;
    }
  }, [canvasSize]);

  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoords(e);
    if (!coords) return;

    if (tool === "text-select") {
      setTextBoxDrawing(true);
      setTextBoxStart(coords);
      setTextBoxEnd(coords);
      setTextBoxConfirmed(null);
      setTextSidebarOpen(false);
      e.preventDefault();
      return;
    }

    setIsDrawing(true);
    if (tool === "draw") {
      const newStrokes = [...maskStrokes, { x: coords.x, y: coords.y, size: brushSize }];
      setMaskStrokes(newStrokes);
    } else if (tool === "erase") {
      const newStrokes = maskStrokes.filter((s) => {
        const dist = Math.sqrt((s.x - coords.x) ** 2 + (s.y - coords.y) ** 2);
        return dist > brushSize / 2;
      });
      setMaskStrokes(newStrokes);
    }
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoords(e);
    if (!coords) return;

    if (tool === "text-select" && textBoxDrawing) {
      setTextBoxEnd(coords);
      return;
    }

    if (!isDrawing) return;

    if (tool === "draw") {
      setMaskStrokes((prev) => [...prev, { x: coords.x, y: coords.y, size: brushSize }]);
    } else if (tool === "erase") {
      setMaskStrokes((prev) =>
        prev.filter((s) => {
          const dist = Math.sqrt((s.x - coords.x) ** 2 + (s.y - coords.y) ** 2);
          return dist > brushSize / 2;
        })
      );
    }
  };

  const handleMouseUp = async () => {
    if (tool === "text-select" && textBoxDrawing && textBoxStart && textBoxEnd) {
      setTextBoxDrawing(false);
      const bx = Math.min(textBoxStart.x, textBoxEnd.x);
      const by = Math.min(textBoxStart.y, textBoxEnd.y);
      const bw = Math.abs(textBoxEnd.x - textBoxStart.x);
      const bh = Math.abs(textBoxEnd.y - textBoxStart.y);
      setTextBoxStart(null);
      setTextBoxEnd(null);

      if (bw > 20 && bh > 10) {
        setTextBoxConfirmed({ x: bx, y: by, w: bw, h: bh });
      } else {
        toast({ title: "Selection too small", description: "Draw a larger box around the text area.", variant: "destructive" });
      }
      return;
    }

    if (isDrawing) {
      setIsDrawing(false);
      addToHistory(maskStrokesRef.current);
    }
  };

  const generateMaskDataUrl = (): string => {
    if (!baseImage) return "";
    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = baseImage.naturalWidth;
    maskCanvas.height = baseImage.naturalHeight;
    const ctx = maskCanvas.getContext("2d");
    if (!ctx) return "";

    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);

    ctx.fillStyle = "#FFFFFF";
    maskStrokes.forEach((stroke) => {
      ctx.beginPath();
      ctx.arc(stroke.x / scale, stroke.y / scale, (stroke.size / 2) / scale, 0, 2 * Math.PI);
      ctx.fill();
    });

    return maskCanvas.toDataURL("image/png");
  };

  const submitInpaint = async () => {
    if (!creative?.imageUrl || maskStrokes.length === 0) {
      toast({ title: "Draw a mask first", description: "Paint over the area you want to change.", variant: "destructive" });
      return;
    }
    if (!prompt.trim()) {
      toast({ title: "Enter a prompt", description: "Describe what should appear in the painted area.", variant: "destructive" });
      return;
    }

    setInpainting(true);
    try {
      const maskDataUrl = generateMaskDataUrl();
      const currentImageUrl = inpaintResult || creative.imageUrl;

      let referenceImageUrl: string | undefined;
      if (refImage) {
        const formData = new FormData();
        formData.append("file", refImage);
        const uploadRes = await fetch("/api/upload", { method: "POST", body: formData });
        if (!uploadRes.ok) {
          setInpainting(false);
          toast({ title: "Upload failed", description: "Could not upload reference image. Please try again.", variant: "destructive" });
          return;
        }
        const uploadData = await uploadRes.json();
        referenceImageUrl = uploadData.url;
      }

      const res = await apiRequest("POST", "/api/creatives/inpaint", {
        imageUrl: currentImageUrl,
        maskDataUrl,
        prompt: prompt.trim(),
        creativeId: selectedCreativeId,
        imageModel,
        referenceImageUrl,
      });
      const data = await res.json();
      if (data.url) {
        setInpaintResult(data.url);
        await refetchVersions();
        setEditHistoryIndex(-1);
        resetMaskState();
        if (refImagePreview) URL.revokeObjectURL(refImagePreview);
        setRefImage(null);
        setRefImagePreview(null);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          setInpaintResultImage(img);
          setBaseImage(img);
          toast({ title: "Edit applied", description: "Your changes have been applied. Keep editing or save the result." });
        };
        img.src = proxyUrl(data.url);
      }
    } catch (error: any) {
      toast({ title: "Edit failed", description: error.message, variant: "destructive" });
    } finally {
      setInpainting(false);
    }
  };

  const generateTextMaskDataUrl = (box: { x: number; y: number; w: number; h: number }): string => {
    if (!baseImage) return "";
    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = baseImage.naturalWidth;
    maskCanvas.height = baseImage.naturalHeight;
    const ctx = maskCanvas.getContext("2d");
    if (!ctx) return "";

    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(box.x / scale, box.y / scale, box.w / scale, box.h / scale);

    return maskCanvas.toDataURL("image/png");
  };

  const suggestHeadlines = async () => {
    if (!creative?.brandId) {
      toast({ title: "No brand linked", description: "This creative needs a brand to suggest headlines.", variant: "destructive" });
      return;
    }
    setSuggestingHeadlines(true);
    try {
      const res = await apiRequest("POST", "/api/creatives/suggest-headlines", {
        brandId: creative.brandId,
        productId: creative.productId || undefined,
        adCopy: creative.adCopy || undefined,
      });
      const data = await res.json();
      if (data.headlines && data.headlines.length > 0) {
        setTaglines(data.headlines);
        toast({ title: "Headlines generated", description: "5 AI-suggested headlines ready. Edit or generate variations." });
      }
    } catch (error: any) {
      toast({ title: "Suggestion failed", description: error.message, variant: "destructive" });
    } finally {
      setSuggestingHeadlines(false);
    }
  };

  useEffect(() => {
    if (!variationsMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (variationsMenuRef.current && !variationsMenuRef.current.contains(e.target as Node)) {
        setVariationsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [variationsMenuOpen]);

  useEffect(() => {
    if (!stylesMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (stylesMenuRef.current && !stylesMenuRef.current.contains(e.target as Node)) {
        setStylesMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [stylesMenuOpen]);

  const submitStyleVariations = async () => {
    if (!selectedCreativeId || generatingStyles || selectedStyles.size === 0) return;
    const brandId = creative?.brandId;
    if (!brandId) {
      toast({ title: "No brand set", description: "This creative needs a brand to generate style variations.", variant: "destructive" });
      return;
    }
    setStylesMenuOpen(false);
    setGeneratingStyles(true);
    const totalJobs = selectedStyles.size * stylesQuantity;
    toast({ title: `Generating ${totalJobs} style variation${totalJobs > 1 ? "s" : ""}`, description: "Style variations will appear in the bottom bar as they complete." });

    try {
      await apiRequest("POST", `/api/creatives/${selectedCreativeId}/style-variations`, {
        styles: Array.from(selectedStyles),
        quantity: stylesQuantity,
        brandId,
        productId: creative?.productId || null,
        imageModel,
      });
      queryClient.invalidateQueries({ queryKey: [`/api/creative-groups/${creative?.groupId}/creatives`] });
    } catch (error: any) {
      toast({ title: "Style variation error", description: error.message || "Failed to generate", variant: "destructive" });
    }
    setGeneratingStyles(false);
    setSelectedStyles(new Set());
  };

  const submitCreateVariations = async () => {
    if (!selectedCreativeId || creatingVariations) return;
    setVariationsMenuOpen(false);
    setCreatingVariations(true);
    setVariationsCreationProgress(`Analyzing ad and generating ${variationsQuantity} variation${variationsQuantity > 1 ? "s" : ""}...`);

    try {
      const res = await apiRequest("POST", "/api/creatives/create-variations", {
        creativeId: selectedCreativeId,
        quantity: variationsQuantity,
        creativity: variationsCreativity,
        imageModel,
      });
      const data = await res.json();
      const completed = data.variations?.filter((v: any) => v.status === "completed")?.length || 0;
      if (completed > 0) {
        toast({ title: "Variations created", description: `${completed} variation${completed > 1 ? "s" : ""} generated. Check the bottom timeline.` });
        await refetchVersions();
        setEditHistoryIndex(-1);
      } else {
        toast({ title: "Generation failed", description: "No variations were generated successfully.", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Variation error", description: error.message || "Failed to create variations", variant: "destructive" });
    } finally {
      setCreatingVariations(false);
      setVariationsCreationProgress(null);
    }
  };

  const submitTextVariations = async () => {
    const validTaglines = taglines.filter((t) => t.trim());
    if (!creative?.imageUrl || !textBoxConfirmed || validTaglines.length === 0) {
      toast({ title: "Missing info", description: "Select a text area and add at least one tagline.", variant: "destructive" });
      return;
    }

    setGeneratingVariations(true);
    setVariationProgress({ current: 0, total: validTaglines.length });

    const maskDataUrl = generateTextMaskDataUrl(textBoxConfirmed);
    const currentImageUrl = inpaintResult || creative.imageUrl;
    let successCount = 0;

    for (let i = 0; i < validTaglines.length; i++) {
      setVariationProgress({ current: i + 1, total: validTaglines.length });
      try {
        const res = await apiRequest("POST", "/api/creatives/inpaint", {
          imageUrl: currentImageUrl,
          maskDataUrl,
          prompt: `Remove the existing text in this area and write "${validTaglines[i].trim()}" instead. Keep the exact same background — do NOT add any colored rectangle, box, banner, or background shape behind the text. The new text should appear directly on the existing background, matching the original style and font.`,
          creativeId: selectedCreativeId,
          imageModel,
        });
        const data = await res.json();
        if (data.url) successCount++;
      } catch (error: any) {
        toast({ title: `Variation ${i + 1} failed`, description: error.message, variant: "destructive" });
      }
    }

    await refetchVersions();
    setEditHistoryIndex(-1);

    if (successCount > 0) {
      toast({ title: "Variations generated", description: `${successCount} text variation${successCount > 1 ? "s" : ""} created. Check the history bar below.` });
    }

    setTextBoxConfirmed(null);
    setTextSidebarOpen(false);
    setGeneratingVariations(false);
    setVariationProgress(null);
    setTaglines([""]);
  };

  const switchToHistoryEntry = (index: number) => {
    if (index < 0 || index >= editHistory.length) return;
    setEditHistoryIndex(index);
    const entry = editHistory[index];
    if (index === 0 && creative?.imageUrl) {
      setInpaintResult(null);
    } else {
      setInpaintResult(entry.imageUrl);
    }
    resetMaskState();
    setPrompt("");

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setBaseImage(img);
      setInpaintResultImage(index === 0 ? null : img);
    };
    img.src = proxyUrl(entry.imageUrl);
  };

  const saveAsNewCreative = async () => {
    if (!inpaintResult || !creative) return;
    try {
      const res = await apiRequest("POST", "/api/creatives/generate", {
        prompt: prompt || "Edited version",
        cloneImageUrl: inpaintResult,
        brandId: creative.brandId || undefined,
        productId: creative.productId || undefined,
        aspectRatio: creative.aspectRatio || "1:1",
        resolution: creative.resolution || "2048",
        quantity: 1,
        groupId: creative.groupId || undefined,
      });
      await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/creatives"] });
      if (creative.groupId) {
        queryClient.invalidateQueries({ queryKey: [`/api/creative-groups/${creative.groupId}/creatives`] });
      }
      toast({ title: "Saved as new creative", description: "The edited image will appear in your library." });
    } catch (error: any) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    }
  };

  const handleDownload = async () => {
    const url = inpaintResult || creative?.imageUrl;
    if (!url) return;
    try {
      const res = await fetch(proxyUrl(url));
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `ad-${creative?.id || "edit"}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        if (e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
        if (e.key === "z" && e.shiftKey) { e.preventDefault(); redo(); }
        if (e.key === "y") { e.preventDefault(); redo(); }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [undo, redo]);

  const canGenerate = maskStrokes.length > 0 && prompt.trim().length > 0 && !inpainting;

  return (
    <div className="h-screen w-screen flex flex-col bg-white overflow-hidden">
      <div className="h-14 border-b border-gray-200 flex items-center justify-between px-4 flex-shrink-0 bg-white z-10">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate("/canvas")}
            data-testid="button-back-editor"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-sm font-semibold text-gray-900">Edit Creative</h1>
          {creative?.aspectRatio && (
            <Badge variant="secondary" className="text-[10px] bg-gray-100">{creative.aspectRatio}</Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="tool-draw"
                  variant={tool === "draw" ? "default" : "ghost"}
                  size="icon"
                  className={cn("h-8 w-8 rounded-md", tool === "draw" && "bg-primary text-white shadow-sm")}
                  onClick={() => setTool("draw")}
                  disabled={inpainting}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Draw mask (paint over area to edit)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="tool-erase"
                  variant={tool === "erase" ? "default" : "ghost"}
                  size="icon"
                  className={cn("h-8 w-8 rounded-md", tool === "erase" && "bg-primary text-white shadow-sm")}
                  onClick={() => setTool("erase")}
                  disabled={inpainting}
                >
                  <Eraser className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Eraser</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="tool-text-select"
                  variant={tool === "text-select" ? "default" : "ghost"}
                  size="icon"
                  className={cn("h-8 w-8 rounded-md", tool === "text-select" && "bg-orange-500 text-white shadow-sm hover:bg-orange-600")}
                  onClick={() => {
                    setTool("text-select");
                    setTextBoxConfirmed(null);
                    setTextSidebarOpen(false);
                  }}
                  disabled={inpainting || generatingVariations}
                >
                  <Type className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Text variations (draw box around text to swap)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="tool-angles"
                  variant="ghost"
                  size="icon"
                  className={cn("h-8 w-8 rounded-md", anglesSidebarOpen && "bg-amber-500 text-white shadow-sm hover:bg-amber-600")}
                  onClick={() => {
                    setAnglesSidebarOpen(!anglesSidebarOpen);
                    setSelectedAngleIds(new Set());
                  }}
                  disabled={inpainting || generatingVariations || !creative?.brandId}
                >
                  <Lightbulb className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reimagine Headline (swap headline using saved angles)</TooltipContent>
            </Tooltip>
          </div>

          <div className="h-6 w-px bg-gray-200" />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={undo} disabled={historyIndex <= 0 || inpainting}>
                <Undo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Undo (Ctrl+Z)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={redo} disabled={historyIndex >= history.length - 1 || inpainting}>
                <Redo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Redo (Ctrl+Shift+Z)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={clearMask} disabled={maskStrokes.length === 0 || inpainting}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Clear mask</TooltipContent>
          </Tooltip>

          <div className="h-6 w-px bg-gray-200" />

          {(tool === "draw" || tool === "erase") && (
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
              <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Size</span>
              <Slider
                value={[brushSize]}
                onValueChange={([v]) => setBrushSize(v)}
                min={4}
                max={120}
                step={2}
                className="w-24"
              />
              <span className="text-xs text-gray-600 font-mono w-6 text-right">{brushSize}</span>
            </div>
          )}

          <div className="h-6 w-px bg-gray-200" />

          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleDownload} data-testid="button-download">
            <Download className="h-3.5 w-3.5" />
            Download
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-20 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col overflow-y-auto py-2 px-2 gap-2">
          {sidebarCreatives.map((c) => (
            <button
              key={c.id}
              data-testid={`sidebar-creative-${c.id}`}
              className={cn(
                "w-full aspect-square rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 hover:opacity-90",
                selectedCreativeId === c.id
                  ? "border-primary shadow-md ring-2 ring-primary/20"
                  : "border-transparent hover:border-gray-300"
              )}
              onClick={() => {
                setSelectedCreativeId(c.id);
                setEditHistoryIndex(-1);
                setTextBoxConfirmed(null);
                setTextSidebarOpen(false);
              }}
            >
              <img
                src={proxyUrl(c.imageUrl || "")}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 flex overflow-hidden">
          <div
            className="flex-1 flex items-center justify-center overflow-hidden relative"
            style={{
              backgroundImage: "radial-gradient(circle, #e5e7eb 1px, transparent 1px)",
              backgroundSize: "20px 20px",
              backgroundColor: "#fafafa",
            }}
          >
            {inpainting && (
              <div className="absolute inset-0 z-20 bg-white/50 flex items-center justify-center">
                <div className="bg-white rounded-xl px-6 py-4 shadow-lg flex items-center gap-3 border">
                  <Loader2 className="h-5 w-5 text-primary animate-spin" />
                  <span className="text-sm font-medium">Applying edit...</span>
                </div>
              </div>
            )}
            {generatingVariations && (
              <div className="absolute inset-0 z-20 bg-white/50 flex items-center justify-center">
                <div className="bg-white rounded-xl px-6 py-4 shadow-lg flex items-center gap-3 border">
                  <Loader2 className="h-5 w-5 text-orange-500 animate-spin" />
                  <span className="text-sm font-medium">
                    {variationProgress ? `Generating variation ${variationProgress.current} of ${variationProgress.total}...` : "Generating text variations..."}
                  </span>
                </div>
              </div>
            )}
            <div className="relative">
              <canvas
                ref={canvasRef}
                className={cn(
                  "block rounded-lg shadow-xl bg-white",
                  tool === "draw" && "cursor-crosshair",
                  tool === "erase" && "cursor-crosshair",
                  tool === "text-select" && "cursor-crosshair",
                  (inpainting || generatingVariations) && "cursor-wait pointer-events-none",
                )}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
              <canvas ref={maskCanvasRef} className="hidden" />

              {textBoxConfirmed && !textSidebarOpen && (
                <button
                  data-testid="button-text-variations-open"
                  className="absolute z-10 bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-3 py-1.5 shadow-lg flex items-center gap-1.5 text-xs font-medium transition-colors"
                  style={{
                    left: textBoxConfirmed.x + textBoxConfirmed.w / 2 - 50,
                    top: textBoxConfirmed.y - 36,
                  }}
                  onClick={() => {
                    setTextSidebarOpen(true);
                    setTaglines([""]);
                  }}
                >
                  <Type className="h-3.5 w-3.5" />
                  Text Variations
                </button>
              )}
            </div>

            {maskStrokes.length > 0 && !inpainting && (
              <button
                className="absolute bottom-4 text-xs text-gray-500 hover:text-gray-700 underline"
                onClick={clearMask}
              >
                Clear all
              </button>
            )}
          </div>

          {anglesSidebarOpen && (
            <div className="w-72 flex-shrink-0 border-l border-gray-200 bg-white flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-semibold text-gray-900">Ad Angles</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setAnglesSidebarOpen(false)}
                  data-testid="button-close-angles-sidebar"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-3">
                <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Saved Angles</span>
                <p className="text-[11px] text-gray-400 mt-0.5 mb-3">Select angles to reimagine the ad headline</p>
                {savedAngles.length === 0 ? (
                  <div className="text-center py-8">
                    <Lightbulb className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs text-gray-400">No saved angles yet</p>
                    <p className="text-[11px] text-gray-400 mt-1">Save angles from Competitor Analysis first</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {savedAngles.map((angle) => (
                      <label
                        key={angle.id}
                        data-testid={`angle-item-${angle.id}`}
                        className={cn(
                          "flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors",
                          selectedAngleIds.has(angle.id)
                            ? "border-amber-300 bg-amber-50"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        )}
                      >
                        <Checkbox
                          data-testid={`checkbox-angle-${angle.id}`}
                          checked={selectedAngleIds.has(angle.id)}
                          onCheckedChange={(checked) => {
                            setSelectedAngleIds((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(angle.id);
                              else next.delete(angle.id);
                              return next;
                            });
                          }}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 leading-tight">{angle.hookHeadline}</p>
                          {angle.hookDescription && (
                            <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{angle.hookDescription}</p>
                          )}
                          {angle.sourceType && (
                            <Badge variant="secondary" className="mt-1 text-[9px] h-4 px-1.5">{angle.sourceType}</Badge>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {savedAngles.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-100">
                  <p className="text-[11px] text-gray-400 mb-2">
                    AI will find and replace the headline text in your ad with each selected angle.
                  </p>
                  <Button
                    data-testid="button-generate-from-angles"
                    className="w-full h-10 gap-2 text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white"
                    onClick={async () => {
                      const selected = savedAngles.filter((a) => selectedAngleIds.has(a.id));
                      if (selected.length === 0) {
                        toast({ title: "No angles selected", description: "Check at least one angle to use.", variant: "destructive" });
                        return;
                      }
                      if (!creative?.imageUrl) return;

                      setGeneratingVariations(true);
                      setVariationProgress({ current: 0, total: selected.length });
                      const currentImageUrl = inpaintResult || creative.imageUrl;
                      let successCount = 0;

                      for (let i = 0; i < selected.length; i++) {
                        setVariationProgress({ current: i + 1, total: selected.length });
                        try {
                          const res = await apiRequest("POST", "/api/creatives/reimagine-headline", {
                            imageUrl: currentImageUrl,
                            headline: selected[i].hookHeadline,
                            creativeId: selectedCreativeId,
                          });
                          const data = await res.json();
                          if (data.url) successCount++;
                        } catch (error: any) {
                          toast({ title: `Angle ${i + 1} failed`, description: error.message, variant: "destructive" });
                        }
                      }

                      await refetchVersions();
                      setEditHistoryIndex(-1);

                      if (successCount > 0) {
                        toast({ title: "Angles applied", description: `${successCount} headline variation${successCount > 1 ? "s" : ""} created. Check the history bar below.` });
                      }

                      setAnglesSidebarOpen(false);
                      setGeneratingVariations(false);
                      setVariationProgress(null);
                      setSelectedAngleIds(new Set());
                    }}
                    disabled={selectedAngleIds.size === 0 || generatingVariations}
                  >
                    {generatingVariations ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {variationProgress ? `Generating ${variationProgress.current}/${variationProgress.total}...` : "Generating..."}
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        Reimagine {selectedAngleIds.size > 0 ? `${selectedAngleIds.size} Headline${selectedAngleIds.size !== 1 ? "s" : ""}` : "Headlines"}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {textSidebarOpen && (
            <div className="w-72 flex-shrink-0 border-l border-gray-200 bg-white flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Type className="h-4 w-4 text-orange-500" />
                  <span className="text-sm font-semibold text-gray-900">Text Variations</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => {
                    setTextSidebarOpen(false);
                    setTextBoxConfirmed(null);
                  }}
                  data-testid="button-close-text-sidebar"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {textBoxConfirmed && baseImage && (
                <div className="px-4 py-3 border-b border-gray-100">
                  <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Selected Area</span>
                  <div className="mt-1.5 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                    <canvas
                      ref={(el) => {
                        if (!el || !baseImage) return;
                        const ctx = el.getContext("2d");
                        if (!ctx) return;
                        const sx = textBoxConfirmed.x / scale;
                        const sy = textBoxConfirmed.y / scale;
                        const sw = textBoxConfirmed.w / scale;
                        const sh = textBoxConfirmed.h / scale;
                        el.width = 240;
                        el.height = (sh / sw) * 240;
                        ctx.drawImage(baseImage, sx, sy, sw, sh, 0, 0, el.width, el.height);
                      }}
                      className="w-full"
                    />
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-y-auto px-4 py-3">
                <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Taglines</span>
                <p className="text-[11px] text-gray-400 mt-0.5 mb-3">Add text variations to replace the selected area</p>
                <div className="space-y-2">
                  {taglines.map((tagline, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <span className="text-[10px] text-gray-400 font-mono w-4 text-right">{i + 1}</span>
                      <Input
                        data-testid={`input-tagline-${i}`}
                        placeholder={`Tagline ${i + 1}...`}
                        value={tagline}
                        onChange={(e) => {
                          const next = [...taglines];
                          next[i] = e.target.value;
                          setTaglines(next);
                        }}
                        className="h-9 text-sm flex-1"
                        disabled={generatingVariations}
                      />
                      {taglines.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 flex-shrink-0 text-gray-400 hover:text-red-500"
                          onClick={() => setTaglines(taglines.filter((_, j) => j !== i))}
                          disabled={generatingVariations}
                          data-testid={`button-remove-tagline-${i}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-xs text-gray-500 gap-1 w-full justify-start hover:text-gray-700"
                  onClick={() => setTaglines([...taglines, ""])}
                  disabled={generatingVariations || taglines.length >= 8}
                  data-testid="button-add-tagline"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add tagline
                </Button>
              </div>

              <div className="px-4 py-3 border-t border-gray-100 flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-testid="button-suggest-headlines"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 flex-shrink-0 border-orange-200 hover:bg-orange-50 hover:border-orange-300"
                      onClick={suggestHeadlines}
                      disabled={suggestingHeadlines || generatingVariations}
                    >
                      {suggestingHeadlines ? (
                        <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                      ) : (
                        <Wand2 className="h-4 w-4 text-orange-500" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">AI-suggest 5 headlines from brand &amp; product</TooltipContent>
                </Tooltip>
                <Button
                  data-testid="button-generate-variations"
                  className="flex-1 h-10 gap-2 text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={submitTextVariations}
                  disabled={generatingVariations || taglines.filter((t) => t.trim()).length === 0}
                >
                  {generatingVariations ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {variationProgress ? `Generating ${variationProgress.current}/${variationProgress.total}...` : "Generating..."}
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Generate {taglines.filter((t) => t.trim()).length} Variation{taglines.filter((t) => t.trim()).length !== 1 ? "s" : ""}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
          </div>

          {(editHistory.length > 1 || styleVariationsForCurrent.length > 0 || sourceEntry) && (
            <div className="h-20 border-t border-gray-200 bg-white flex items-center px-4 gap-3 flex-shrink-0 overflow-x-auto">
              {sourceEntry && (
                <button
                  data-testid="button-back-to-original"
                  className={cn(
                    "relative h-14 w-14 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0",
                    "border-gray-200 hover:border-gray-400"
                  )}
                  onClick={() => {
                    setSelectedCreativeId(sourceEntry.creativeId!);
                    setEditHistoryIndex(-1);
                    setTextBoxConfirmed(null);
                    setTextSidebarOpen(false);
                  }}
                >
                  <img src={proxyUrl(sourceEntry.imageUrl)} alt="Original" className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[8px] py-0.5 text-center leading-none">Original</span>
                </button>
              )}
              {!sourceEntry && editHistory.map((entry, i) => (
                <div key={i} className="relative flex-shrink-0 group/hist">
                  <button
                    data-testid={`history-entry-${i}`}
                    className={cn(
                      "relative h-14 w-14 rounded-lg overflow-hidden border-2 transition-all",
                      (editHistoryIndex === i || (editHistoryIndex === -1 && i === editHistory.length - 1))
                        ? "border-primary ring-2 ring-primary/20 shadow-md"
                        : "border-gray-200 hover:border-gray-400"
                    )}
                    onClick={() => switchToHistoryEntry(i)}
                  >
                    <img
                      src={proxyUrl(entry.imageUrl)}
                      alt={entry.prompt}
                      className="w-full h-full object-cover"
                    />
                    {i === 0 && (
                      <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[8px] py-0.5 text-center leading-none">Original</span>
                    )}
                  </button>
                  {i > 0 && entry.id && (
                    <button
                      data-testid={`button-delete-history-${i}`}
                      className="absolute -top-1 -left-1 h-5 w-5 rounded-full bg-red-500 border border-red-600 shadow-sm flex items-center justify-center opacity-0 group-hover/hist:opacity-100 transition-opacity hover:bg-red-600 z-10"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await fetch(`/api/creative-versions/${entry.id}`, { method: "DELETE" });
                          await refetchVersions();
                          if (editHistoryIndex === i || (editHistoryIndex === -1 && i === editHistory.length - 1)) {
                            const newLastIndex = editHistory.length - 2;
                            if (newLastIndex <= 0) {
                              setInpaintResult(null);
                              setEditHistoryIndex(-1);
                              const img = new Image();
                              img.crossOrigin = "anonymous";
                              img.onload = () => { setBaseImage(img); setInpaintResultImage(null); };
                              img.src = proxyUrl(creative?.imageUrl || "");
                            } else {
                              setEditHistoryIndex(-1);
                            }
                          }
                        } catch {}
                      }}
                    >
                      <X className="h-3 w-3 text-white" />
                    </button>
                  )}
                  <button
                    data-testid={`button-download-history-${i}`}
                    className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center opacity-0 group-hover/hist:opacity-100 transition-opacity hover:bg-gray-100"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const res = await fetch(proxyUrl(entry.imageUrl));
                        const blob = await res.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = blobUrl;
                        a.download = `ad-${creative?.id || "edit"}-v${i}.png`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(blobUrl);
                      } catch {
                        window.open(entry.imageUrl, "_blank");
                      }
                    }}
                  >
                    <Download className="h-3 w-3 text-gray-600" />
                  </button>
                </div>
              ))}
              {(completedStyleVariations.length > 0 || generatingStyleVariations.length > 0) && !sourceEntry && editHistory.length > 0 && (
                <div className="h-10 w-px bg-gray-200 flex-shrink-0" />
              )}
              {completedStyleVariations.map((c) => (
                <div key={`style-${c.id}`} className="relative flex-shrink-0 group/hist">
                  <button
                    data-testid={`style-variation-${c.id}`}
                    className={cn(
                      "relative h-14 w-14 rounded-lg overflow-hidden border-2 transition-all",
                      selectedCreativeId === c.id
                        ? "border-indigo-500 ring-2 ring-indigo-500/20 shadow-md"
                        : "border-gray-200 hover:border-indigo-300"
                    )}
                    onClick={() => {
                      setSelectedCreativeId(c.id);
                      setEditHistoryIndex(-1);
                      setTextBoxConfirmed(null);
                      setTextSidebarOpen(false);
                    }}
                  >
                    <img
                      src={proxyUrl(c.imageUrl || "")}
                      alt={c.adStyle || ""}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <span className="absolute bottom-0 inset-x-0 bg-indigo-600/90 text-white text-[7px] py-0.5 text-center leading-none truncate px-0.5">
                      {c.adStyle}
                    </span>
                  </button>
                  <button
                    className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center opacity-0 group-hover/hist:opacity-100 transition-opacity hover:bg-gray-100"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const res = await fetch(proxyUrl(c.imageUrl || ""));
                        const blob = await res.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = blobUrl;
                        a.download = `style-${c.adStyle}-${c.id}.png`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(blobUrl);
                      } catch {
                        window.open(c.imageUrl || "", "_blank");
                      }
                    }}
                  >
                    <Download className="h-3 w-3 text-gray-600" />
                  </button>
                </div>
              ))}
              {generatingStyleVariations.map((c) => (
                <div key={`gen-${c.id}`} className="flex-shrink-0 h-14 w-14 rounded-lg border-2 border-dashed border-indigo-300 bg-indigo-50 flex flex-col items-center justify-center gap-1">
                  <Loader2 className="h-3 w-3 text-indigo-400 animate-spin" />
                  <span className="text-[7px] text-indigo-500 font-medium truncate w-full text-center px-0.5">{c.adStyle}</span>
                </div>
              ))}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex-shrink-0 h-14 w-14 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-gray-400 hover:text-gray-500 cursor-default">
                    <Undo2 className="h-4 w-4" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>Edit history — click any version to go back to it</TooltipContent>
              </Tooltip>
            </div>
          )}

          <div className="border-t border-gray-200 bg-white px-6 py-4 flex-shrink-0">
            <div className="flex items-center gap-3 max-w-3xl mx-auto">
              <Select value={imageModel} onValueChange={(v: "seedream-v5-lite" | "nano-banana-2") => setImageModel(v)}>
                <SelectTrigger className="w-[150px] h-12 rounded-xl border-gray-200 bg-gray-50/50 text-xs shrink-0" data-testid="select-editor-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="seedream-v5-lite">Seedream V5 Lite</SelectItem>
                  <SelectItem value="nano-banana-2">NanoBanana 2</SelectItem>
                </SelectContent>
              </Select>
              <input
                ref={refImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (refImagePreview) URL.revokeObjectURL(refImagePreview);
                    setRefImage(file);
                    setRefImagePreview(URL.createObjectURL(file));
                  }
                  e.target.value = "";
                }}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="button-ref-image"
                    size="icon"
                    variant="outline"
                    className={cn(
                      "h-12 w-12 rounded-xl flex-shrink-0",
                      refImagePreview ? "border-primary bg-primary/5" : "border-gray-200 bg-gray-50/50"
                    )}
                    onClick={() => refImageInputRef.current?.click()}
                    disabled={inpainting}
                  >
                    <ImagePlus className={cn("h-5 w-5", refImagePreview ? "text-primary" : "text-gray-500")} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Add reference image</TooltipContent>
              </Tooltip>
              {refImagePreview && (
                <div className="relative h-12 w-12 rounded-xl overflow-hidden border border-primary/30 flex-shrink-0">
                  <img src={refImagePreview} alt="Reference" className="h-full w-full object-cover" />
                  <button
                    data-testid="button-remove-ref-image"
                    className="absolute -top-1 -right-1 h-5 w-5 bg-gray-800 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    onClick={() => { if (refImagePreview) URL.revokeObjectURL(refImagePreview); setRefImage(null); setRefImagePreview(null); }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              <Input
                data-testid="input-edit-prompt"
                placeholder="Enter your prompt here..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canGenerate) submitInpaint();
                }}
                className="flex-1 h-12 rounded-xl border-gray-200 focus:border-primary/50 bg-gray-50/50 text-sm"
                disabled={inpainting}
              />
              <Button
                data-testid="button-generate-edit"
                className={cn(
                  "h-12 px-6 rounded-xl gap-2 text-sm font-medium shadow-lg",
                  canGenerate
                    ? "bg-primary hover:bg-primary/90 text-white shadow-primary/20"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
                )}
                onClick={submitInpaint}
                disabled={!canGenerate}
              >
                {inpainting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Generate
              </Button>
              <div className="relative" ref={variationsMenuRef}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-testid="button-create-variations"
                      size="icon"
                      className={cn(
                        "h-12 w-12 rounded-xl flex-shrink-0 shadow-lg",
                        creatingVariations
                          ? "bg-amber-500 text-white shadow-amber-200"
                          : "bg-white border border-gray-200 text-amber-600 hover:bg-amber-50 shadow-sm"
                      )}
                      onClick={() => {
                        if (!creatingVariations) setVariationsMenuOpen(!variationsMenuOpen);
                      }}
                      disabled={creatingVariations || !selectedCreativeId}
                    >
                      {creatingVariations ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-5 w-5" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{creatingVariations ? variationsCreationProgress : "Create copy/text variations"}</TooltipContent>
                </Tooltip>

                {variationsMenuOpen && (
                  <div className="absolute bottom-14 right-0 w-64 bg-white rounded-xl border border-gray-200 shadow-xl p-4 z-50">
                    <p className="text-xs font-semibold text-gray-700 mb-3">Create Variations</p>
                    <p className="text-[11px] text-gray-500 mb-3">AI will analyze your ad and create variations</p>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs text-gray-600">Qty:</span>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4].map((n) => (
                          <button
                            key={n}
                            data-testid={`button-qty-${n}`}
                            className={cn(
                              "h-8 w-8 rounded-lg text-xs font-medium transition-colors",
                              variationsQuantity === n
                                ? "bg-amber-500 text-white"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            )}
                            onClick={() => setVariationsQuantity(n)}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-gray-600">Creativity:</span>
                        <span className="text-[10px] font-medium text-amber-600">
                          {variationsCreativity === 0 ? "Copy Only" : variationsCreativity <= 33 ? "Subtle" : variationsCreativity <= 66 ? "Moderate" : "Wild"}
                        </span>
                      </div>
                      <input
                        data-testid="slider-creativity"
                        type="range"
                        min={0}
                        max={100}
                        value={variationsCreativity}
                        onChange={(e) => setVariationsCreativity(Number(e.target.value))}
                        className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-amber-500 bg-gray-200"
                      />
                      <div className="flex justify-between mt-1">
                        <span className="text-[9px] text-gray-400">Copy changes</span>
                        <span className="text-[9px] text-gray-400">Art + colors</span>
                      </div>
                    </div>
                    <Button
                      data-testid="button-start-variations"
                      className="w-full h-9 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium gap-2"
                      onClick={submitCreateVariations}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Generate {variationsQuantity} Variation{variationsQuantity > 1 ? "s" : ""}
                    </Button>
                  </div>
                )}
              </div>
              <div className="relative" ref={stylesMenuRef}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-testid="button-styles"
                      size="icon"
                      className={cn(
                        "h-12 w-12 rounded-xl flex-shrink-0 shadow-lg",
                        generatingStyles
                          ? "bg-indigo-500 text-white shadow-indigo-200"
                          : stylesMenuOpen
                          ? "bg-indigo-600 text-white shadow-indigo-200"
                          : "bg-white border border-gray-200 text-indigo-600 hover:bg-indigo-50 shadow-sm"
                      )}
                      onClick={() => {
                        if (!generatingStyles) setStylesMenuOpen(!stylesMenuOpen);
                      }}
                      disabled={generatingStyles || !selectedCreativeId}
                    >
                      {generatingStyles ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <LayoutGrid className="h-5 w-5" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{generatingStyles ? "Generating style variations..." : "Ad Style Variations"}</TooltipContent>
                </Tooltip>

                {stylesMenuOpen && (
                  <div className="absolute bottom-14 right-0 w-80 bg-white rounded-xl border border-gray-200 shadow-xl p-4 z-50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-700">Create Style Variations</p>
                      <button className="h-5 w-5 rounded-full hover:bg-gray-100 flex items-center justify-center" onClick={() => setStylesMenuOpen(false)}>
                        <X className="h-3 w-3 text-gray-400" />
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-3">Select styles to generate. One ad per style per quantity.</p>
                    <div className="flex flex-wrap gap-1.5 mb-3 max-h-[180px] overflow-y-auto">
                      {AD_STYLES.map((style) => {
                        const isSelected = selectedStyles.has(style);
                        return (
                          <button
                            key={style}
                            data-testid={`style-pill-${style.toLowerCase().replace(/\s+/g, "-")}`}
                            className={cn(
                              "px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border",
                              isSelected
                                ? "bg-indigo-600 text-white border-indigo-600"
                                : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600"
                            )}
                            onClick={() => {
                              setSelectedStyles(prev => {
                                const next = new Set(prev);
                                if (next.has(style)) next.delete(style);
                                else next.add(style);
                                return next;
                              });
                            }}
                          >
                            {style}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs text-gray-600">Qty per style:</span>
                      <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-1.5 py-1">
                        <button
                          className="h-6 w-6 rounded flex items-center justify-center text-gray-500 hover:bg-gray-200 disabled:opacity-40"
                          onClick={() => setStylesQuantity(q => Math.max(1, q - 1))}
                          disabled={stylesQuantity <= 1}
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="text-xs font-semibold text-gray-700 w-4 text-center">{stylesQuantity}</span>
                        <button
                          className="h-6 w-6 rounded flex items-center justify-center text-gray-500 hover:bg-gray-200 disabled:opacity-40"
                          onClick={() => setStylesQuantity(q => Math.min(5, q + 1))}
                          disabled={stylesQuantity >= 5}
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                    {selectedStyles.size > 0 && (
                      <p className="text-[10px] text-indigo-600 font-medium mb-2">
                        {selectedStyles.size} style{selectedStyles.size > 1 ? "s" : ""} × {stylesQuantity} qty = {selectedStyles.size * stylesQuantity} ad{selectedStyles.size * stylesQuantity > 1 ? "s" : ""}
                      </p>
                    )}
                    <Button
                      data-testid="button-generate-styles"
                      className="w-full h-9 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium gap-2"
                      onClick={submitStyleVariations}
                      disabled={selectedStyles.size === 0}
                    >
                      <LayoutGrid className="h-3.5 w-3.5" />
                      Generate {selectedStyles.size * stylesQuantity || 0} Style Variation{selectedStyles.size * stylesQuantity !== 1 ? "s" : ""}
                    </Button>
                  </div>
                )}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="button-ai-chat"
                    size="icon"
                    className={cn(
                      "h-12 w-12 rounded-xl flex-shrink-0 shadow-lg",
                      chatOpen
                        ? "bg-indigo-600 text-white shadow-indigo-200 hover:bg-indigo-700"
                        : "bg-white border border-gray-200 text-indigo-600 hover:bg-indigo-50 shadow-sm"
                    )}
                    onClick={() => setChatOpen(!chatOpen)}
                  >
                    <MessageSquare className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>AI Chat — describe changes in plain language</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      <CreativeChat
        open={chatOpen}
        onOpenChange={setChatOpen}
        imageUrl={inpaintResult || creative?.imageUrl || ""}
        creativeId={selectedCreativeId}
        imageModel={imageModel}
        onImageGenerated={(url) => {
          setInpaintResult(url);
          const img = new window.Image();
          img.crossOrigin = "anonymous";
          img.onload = () => setInpaintResultImage(img);
          img.src = proxyUrl(url);
          refetchVersions();
          resetMaskState();
        }}
      />
    </div>
  );
}
